import { useState, useEffect } from 'react';
import { Calendar, Info, MessageCircle, Layers, Plus, Upload as UploadIcon } from 'lucide-react';
import { formatDateLocale, formatDateTimeLocale } from '@/common/utils/date';
import FechaEscrituraModal from './FechaEscrituraModal';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import PermissionRender from '@/auth/components/PermissionRender';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useVencimientoPresentacion } from '@/hooks/useVencimientoPresentacion';
import WhatsAppReminderModal from './WhatsAppReminderModal';
import WhatsAppEscrituraModal from './WhatsAppEscrituraModal';
import WhatsAppEscrituraInscriptaModal from './WhatsAppEscrituraInscriptaModal';
import UploadDownloadEscritura from './UploadEscritura';
import { EscriturasDialog } from '@/tramite-escrituras';
import { useEscriturasAdicionales } from '@/tramite-escrituras';

const CardEscritura = ({ tramite: tramiteProp, onChange, onTramiteUpdated }) => {

  const { user } = useAuth();
  const userPermissions = user?.permissions ?? [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canSendWhatsApp = checkAllPermissions(userPermissions, [PERMISSIONS.SEND_WHATSAPP.name]);
  const vencimientoPresentacion = useVencimientoPresentacion();

  const [tramite, setTramite] = useState(tramiteProp);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isEscrituraWAOpen, setIsEscrituraWAOpen] = useState(false);
  const [isEscrituraInscriptaWAOpen, setIsEscrituraInscriptaWAOpen] = useState(false);
  const [isAdicionalesOpen, setIsAdicionalesOpen] = useState(false);

  const { total: totalEscriturasAdicionales } = useEscriturasAdicionales({ tramiteId: tramite?.id });

  useEffect(() => {
    setTramite(tramiteProp);
  }, [tramiteProp]);

  const handleChange = (e) => {
    setTramite(e);
    onChange && onChange(e);
  };

  const handleChangeFechaEscritura = (e) => {
    setTramite(e);
    onChange && onChange(e);
  };

  const showEscriturasSection = canManageTramites || tramite.escritura_path || tramite.escritura_pub_path || totalEscriturasAdicionales > 0;

  return (
    <div className="bg-white shadow-md rounded overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-red-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm md:text-base">Escritura</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <div className="text-sm space-y-1">
                    <div><strong>Fecha de escritura:</strong> {tramite.fecescritura ? formatDateLocale(tramite.fecescritura) : 'No establecida'}</div>
                    <div><strong># Escritura:</strong> {tramite.num_escritura || 'No establecido'}</div>
                    <div><strong># Entrada:</strong> {tramite.num_entrada || 'No establecido'}</div>
                    <div><strong>Fecha de entrada:</strong> {tramite.fecentrada ? formatDateLocale(tramite.fecentrada) : 'No establecida'}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <PermissionRender permission='MANAGE_TRAMITES'>
            <FechaEscrituraModal
              tramite={tramite}
              onChange={handleChangeFechaEscritura}
              variant="button"
            />
          </PermissionRender>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Fecha de Firma */}
        {!tramite.escritura_path && (
          <div className="flex items-center justify-between text-xs sm:text-sm gap-2">
            <div className="flex items-center min-w-0 flex-1">
              <Calendar className="mx-1 sm:mx-2 flex-shrink-0" size={14} />
              <span className="min-w-0 break-words">
                {tramite.fecfirma
                  ? `Fecha estimada de firma: ${formatDateTimeLocale(tramite.fecfirma)}`
                  : 'Fecha estimada de firma: No establecida'
                }
              </span>
            </div>
            {canSendWhatsApp && !!tramite.fecfirma && (
              <button
                type="button"
                className="h-7 w-7 shrink-0 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center shadow-sm"
                aria-label="Enviar recordatorio de firma por WhatsApp"
                title="Enviar recordatorio de firma por WhatsApp"
                onClick={() => setIsReminderModalOpen(true)}
              >
                <MessageCircle size={14} />
              </button>
            )}
          </div>
        )}

        {/* Vencimiento presentación */}
        <PermissionRender permission='MANAGE_TRAMITES'>
          {!tramite.escritura_pub_path && (
            <div className="flex items-center text-xs sm:text-sm">
              <Calendar className="mx-1 sm:mx-2 flex-shrink-0" size={14} />
              <span className="min-w-0 break-words">
                {`Vencimiento presentación: ${vencimientoPresentacion}`}
              </span>
            </div>
          )}
        </PermissionRender>

        {/* Fecha de Escritura */}
        <div className="flex items-center justify-between text-xs sm:text-sm">
          <div className="flex items-center min-w-0 flex-1">
            <Calendar className="mx-1 sm:mx-2 flex-shrink-0" size={14} />
            <span className="min-w-0 break-words">
              {tramite.fecescritura
                ? `Fecha escritura: ${formatDateLocale(tramite.fecescritura)}`
                : 'Fecha escritura: No establecida'
              }
            </span>
          </div>
        </div>

        {/* Tres filas de escrituras */}
        {showEscriturasSection && (
          <div className="space-y-2 pt-1">
            <UploadDownloadEscritura
              publicada={false}
              tramite={tramite}
              onChange={handleChange}
              canSendWhatsApp={canSendWhatsApp}
              onSendWhatsApp={() => setIsEscrituraWAOpen(true)}
            />
            <UploadDownloadEscritura
              publicada={true}
              tramite={tramite}
              onChange={handleChange}
              canSendWhatsApp={canSendWhatsApp}
              onSendWhatsApp={() => setIsEscrituraInscriptaWAOpen(true)}
            />

            {/* Fila adicionales */}
            {(canManageTramites || totalEscriturasAdicionales > 0) && (
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Escrituras adicionales</span>
                  {totalEscriturasAdicionales > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                      {totalEscriturasAdicionales}
                    </span>
                  )}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsAdicionalesOpen(true)}
                        className="h-8 w-8 p-0 hover:bg-blue-100"
                      >
                        {totalEscriturasAdicionales > 0
                          ? <Plus className="w-4 h-4 text-blue-600" />
                          : <UploadIcon className="w-4 h-4 text-blue-600" />
                        }
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{totalEscriturasAdicionales > 0 ? 'Ver escrituras adicionales' : 'Agregar escritura adicional'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        )}
      </div>

      {canSendWhatsApp && !!tramite.fecfirma && (
        <WhatsAppReminderModal open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen} tramite={tramite} />
      )}
      {canSendWhatsApp && tramite.escritura_path && (
        <WhatsAppEscrituraModal
          open={isEscrituraWAOpen}
          onOpenChange={setIsEscrituraWAOpen}
          tramite={tramite}
          onTramiteUpdated={() => handleChange(undefined)}
        />
      )}
      {canSendWhatsApp && tramite.escritura_pub_path && (
        <WhatsAppEscrituraInscriptaModal
          open={isEscrituraInscriptaWAOpen}
          onOpenChange={setIsEscrituraInscriptaWAOpen}
          tramite={tramite}
          onTramiteUpdated={() => handleChange(undefined)}
        />
      )}

      <EscriturasDialog
        tramiteId={tramite.id}
        open={isAdicionalesOpen}
        onOpenChange={setIsAdicionalesOpen}
      />
    </div>
  );
};

export default CardEscritura;
